import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  TableContainer,
  Text,
  useDisclosure
} from "@chakra-ui/react";

import { IconType } from "react-icons";
import { FaRegCheckCircle } from "react-icons/fa";
import { FaRegHourglass } from "react-icons/fa6";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { LuMessageCircleMore } from "react-icons/lu";
import {
  PiArrowBendUpRightBold,
  PiChatCircleText,
  PiSealCheck,
  PiStackPlus,
  PiTrash
} from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Id } from "@/jenesys-types/data/common";
import { ListQuerySchema } from "@/jenesys-types/data/communication";
import { DataType } from "@/jenesys-types/data/enums";

import { ConfirmModal } from "@/components/common/controls/ConfirmModal";
import { useCrudListContext } from "@/components/common/crud/CrudList/CrudListContext";
import { CrudListTable } from "@/components/common/crud/CrudList/CrudListTable";
import { ColumnDefinition } from "@/components/common/table/types";
import useActionTrigger from "@/hooks/util/useActionTrigger";
import { useCompany } from "@/hooks/util/useCompany";
import { useUser } from "@/hooks/util/useUser";
import { updateQueryParams } from "@/store/crud/list";
import {
  deleteQuery,
  resolveQuery,
  selectIsDeletingQuery,
  selectIsResolvingQuery
} from "@/store/features/communicationsHub/communicationsHub";
import {
  formatDateForQueryTable,
  lowercaseAllButFirstLetter,
  toastError,
  toastSuccess
} from "@/util/helpers";
import { RESTRICTED_QUERY_ROLES, roleIsOneOf } from "@/util/roles";
import { getQuerySummary } from "@/utils/queryTemplates";

import { NewQueryModal } from "./NewQueryModal";
import { QueryDetailsModal } from "./QueryDetailsModal";
import { QueryFilters } from "./QueryFilters";
import { QueryReassignModal } from "./QueryReassignModal";

interface Props {
  context: "company" | "client";
}

// Status styles mapping
const queryStatusStyles: {
  [key: string]: {
    color: string;
    bg: string;
    icon: IconType;
  };
} = {
  NEW_MESSAGE: {
    color: "blue.600",
    bg: "blue.100",
    icon: LuMessageCircleMore
  },
  AWAITING_RESPONSE: {
    color: "orange.600",
    bg: "orange.100",
    icon: FaRegHourglass
  },
  WAITING_FOR_RESPONSE: {
    color: "orange.600",
    bg: "orange.100",
    icon: FaRegHourglass
  },
  RESOLVED: {
    color: "green.600",
    bg: "green.100",
    icon: FaRegCheckCircle
  },
  CLOSED: {
    color: "green.600",
    bg: "green.100",
    icon: FaRegCheckCircle
  }
};

export const QueriesTable: React.FC<Props> = ({ context }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItemIds, setSelectedItemIds] = useState<Array<Id>>([]);
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedQueryType, setSelectedQueryType] = useState<string>("");

  const {
    isOpen: isNewQueryModalOpen,
    onOpen: onNewQueryModalOpen,
    onClose: onNewQueryModalClose
  } = useDisclosure();
  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose
  } = useDisclosure();
  const resolveQueryDisclosure = useDisclosure();
  const deleteQueryDisclosure = useDisclosure();
  const reassignQueryDisclosure = useDisclosure();

  const { activeCompanyId, activeClientId, selectedCompany, selectedClient } =
    useCompany();
  const { currentUser } = useUser();
  const { refreshList } = useCrudListContext<ListQuerySchema>();

  const isResolvingQuery = useSelector(selectIsResolvingQuery);
  const isDeletingQuery = useSelector(selectIsDeletingQuery);

  // Determine companyId for modal
  const companyId = context === "company" ? activeCompanyId : activeClientId;

  const columns = useMemo<Array<ColumnDefinition<ListQuerySchema>>>(
    () => [
      {
        id: "title",
        header: "QUERY",
        isSortable: true,
        accessor: (query) => {
          const querySummary = getQuerySummary(query.title);
          return querySummary?.title || query.title;
        }
      },
      {
        id: "type",
        header: "QUERY TYPE",
        isSortable: true,
        accessor: (query) => lowercaseAllButFirstLetter(query.type)
      },
      {
        id: "company",
        header: "CLIENT",
        isSortable: true,
        accessor: (query) => query.company?.name || "—"
      },
      {
        id: "description",
        header: "DESCRIPTION",
        isSortable: false,
        accessor: (query) => {
          const querySummary = getQuerySummary(query.title);
          return (
            <Text whiteSpace="normal" noOfLines={3} w={270}>
              {querySummary?.description || query.description}
            </Text>
          );
        }
      },
      {
        id: "status",
        header: "STATUS",
        isSortable: true,
        accessor: (query) => {
          const statusStyle = queryStatusStyles[query.status] || {
            bg: "gray.100",
            color: "gray.600",
            icon: FaRegHourglass
          };

          return (
            <HStack
              backgroundColor={statusStyle.bg}
              borderRadius={12}
              px={2}
              py={1}
              width="fit-content"
            >
              <Icon as={statusStyle.icon} color={statusStyle.color} />
              <Text
                color={statusStyle.color}
                whiteSpace="nowrap"
                fontWeight={500}
                fontSize={14}
              >
                {query.status}
              </Text>
            </HStack>
          );
        }
      },
      {
        id: "created_at",
        header: "CREATED",
        isSortable: true,
        accessor: (query) => {
          const formatted = formatDateForQueryTable(query.created_at);
          return (
            <Box>
              <Text fontWeight={500} fontSize={14}>
                {formatted.date}
              </Text>
              <Text fontSize={14}>{formatted.time}</Text>
            </Box>
          );
        }
      },
      {
        id: "ACTION",
        header: "ACTION",
        accessor: (query) => {
          const isNewMessage = query.status === "NEW_MESSAGE";
          const isResolvedOrClosed =
            query.status === "RESOLVED" || query.status === "CLOSED";

          return (
            <HStack onClick={(e) => e.stopPropagation()}>
              <Button
                width="120px"
                variant={isNewMessage ? "solid" : "outline"}
                borderColor="primary.600"
                fontSize={12}
                fontWeight={600}
                height={9}
                color={isNewMessage ? "white" : "primary.600"}
                onClick={() => {
                  setSelectedQueryId(query.id);
                  onDetailsModalOpen();
                }}
              >
                {isNewMessage && (
                  <Icon as={PiChatCircleText} mr={2} fontSize={20} />
                )}
                {isNewMessage ? "Review" : "View Query"}
              </Button>

              {query.created_by_user_id === currentUser?.id && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    size="sm"
                    h={9}
                    color="gray.800"
                    fontSize={20}
                    variant="default"
                    icon={<HiOutlineDotsVertical />}
                  />
                  <MenuList maxW={200} minW={200}>
                    {!isResolvedOrClosed && (
                      <MenuItem
                        as={HStack}
                        py={2.5}
                        spacing={2}
                        onClick={() => {
                          setSelectedQueryId(query.id);
                          resolveQueryDisclosure.onOpen();
                        }}
                      >
                        <Icon fontSize={20} as={PiSealCheck} />
                        <Text fontSize={14}>Resolve / Close</Text>
                      </MenuItem>
                    )}

                    <MenuItem
                      as={HStack}
                      py={2.5}
                      spacing={2}
                      onClick={() => {
                        setSelectedQueryId(query.id);
                        deleteQueryDisclosure.onOpen();
                      }}
                    >
                      <Icon fontSize={20} as={PiTrash} />
                      <Text fontSize={14}>Delete</Text>
                    </MenuItem>
                    <MenuItem
                      as={HStack}
                      py={2.5}
                      spacing={2}
                      onClick={() => {
                        setSelectedQueryId(query.id);
                        reassignQueryDisclosure.onOpen();
                      }}
                    >
                      <Icon fontSize={20} as={PiArrowBendUpRightBold} />
                      <Text fontSize={14}>Reassign Query </Text>
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </HStack>
          );
        }
      }
    ],
    [
      currentUser?.id,
      onDetailsModalOpen,
      resolveQueryDisclosure,
      deleteQueryDisclosure,
      reassignQueryDisclosure
    ]
  );

  const { listId } = useCrudListContext<ListQuerySchema>();

  const handleStatusClick = useCallback(
    (status: string) => {
      setSelectedStatus(status);
      dispatch(
        updateQueryParams(DataType.CommunicationsQuery, listId, {
          query_status: status || undefined
        })
      );
    },
    [dispatch, listId]
  );

  // Check for query parameter to auto-open modal
  useEffect(() => {
    const openQueryId = searchParams.get("openQueryId");
    if (openQueryId) {
      setSelectedQueryId(openQueryId);
      onDetailsModalOpen();
      // Remove the query parameter after opening the modal
      searchParams.delete("openQueryId");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, onDetailsModalOpen]);

  const handleResolveQuery = () => {
    if (selectedQueryId && companyId) {
      dispatch(
        resolveQuery(companyId, selectedQueryId, () => {
          toastSuccess("Query resolved successfully!");
          resolveQueryDisclosure.onClose();
          refreshList();
          setSelectedQueryId(null);
        })
      );
    }
  };

  const handleDeleteQuery = () => {
    if (selectedQueryId && companyId) {
      dispatch(
        deleteQuery(companyId, selectedQueryId, () => {
          toastSuccess("Query deleted successfully!");
          deleteQueryDisclosure.onClose();
          refreshList();
          setSelectedQueryId(null);
        })
      );
    }
  };

  const { trigger: triggerNotify } = useActionTrigger({
    actionName: "notify",
    dataType: DataType.CommunicationsQuery,
    onSuccess: () => {
      toastSuccess("Notification sent");
      refreshList();
      setSelectedItemIds([]);
    },
    onError: (e) => toastError("Failed to send notification", e)
  });

  const currentRole = selectedClient?.role || selectedCompany?.role || "";
  const isRestricted = roleIsOneOf(currentRole, RESTRICTED_QUERY_ROLES);

  return (
    <Box>
      <HStack alignItems="center" justifyContent="space-between" mb={6}>
        <Box>
          <Text color="secondary.600" fontSize={30} fontWeight={600} mb={2}>
            Your Queries
          </Text>
          <Text fontSize={18}>
            Please review and respond to the queries below.
          </Text>
        </Box>
        {!isRestricted && (
          <Button
            size="md"
            onClick={onNewQueryModalOpen}
            leftIcon={<PiStackPlus />}
          >
            New General Query
          </Button>
        )}
      </HStack>

      <HStack boxShadow="sm" rounded={6} p={6} bg="white" mb={6} mt={5}>
        <QueryFilters
          selectedType={selectedQueryType}
          onTypeChange={setSelectedQueryType}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusClick}
        />

        <Button
          size="sm"
          h={10}
          minW={36}
          variant="solid"
          isDisabled={!selectedItemIds.length || !companyId}
          colorScheme="blue"
          onClick={() => {
            triggerNotify({
              pathParams: { companyId },
              data: { queries_ids: selectedItemIds }
            });
          }}
        >
          Notify Assignees
        </Button>
      </HStack>

      <Card>
        <CardBody padding={0}>
          <TableContainer
            position="relative"
            fontSize={14}
            fontWeight={500}
            rounded={6}
            pt={0}
          >
            <CrudListTable
              columns={columns}
              onRowItemClick={(item) => {
                setSelectedQueryId(item.id);
                onDetailsModalOpen();
              }}
              selectionType="multi"
              selectedItems={selectedItemIds}
              onSelectedItemsChange={setSelectedItemIds}
            />
          </TableContainer>
        </CardBody>
      </Card>

      <NewQueryModal
        isOpen={isNewQueryModalOpen}
        onClose={onNewQueryModalClose}
        context={context}
      />
      <QueryDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          onDetailsModalClose();
          setSelectedQueryId(null);
        }}
        queryId={selectedQueryId || ""}
        companyId={companyId || ""}
      />
      <ConfirmModal
        {...resolveQueryDisclosure}
        title="Resolve Query"
        content="Are you sure you want to resolve this query? This will close the query and mark it as resolved."
        confirmText="Resolve"
        loadingText="Resolving..."
        isLoading={isResolvingQuery}
        confirmAction={handleResolveQuery}
      />
      <ConfirmModal
        {...deleteQueryDisclosure}
        title="Delete Query"
        content="Are you sure you want to delete this query? This action cannot be undone."
        confirmText="Delete"
        loadingText="Deleting..."
        isLoading={isDeletingQuery}
        confirmAction={handleDeleteQuery}
      />
      <QueryReassignModal
        isOpen={reassignQueryDisclosure.isOpen}
        onClose={reassignQueryDisclosure.onClose}
        queryId={selectedQueryId || ""}
        companyId={companyId || ""}
      />
    </Box>
  );
};
